import { cva, type VariantProps } from 'class-variance-authority';
import { useT } from '@transifex/react';

import { cn } from '~/lib/utils';
import { Tooltip, TooltipTrigger, TooltipContent } from '~/components/ui/tooltip';

const starIconVariants = cva('shrink-0', {
  variants: {
    state: {
      filled: '',
      empty: '',
    },
    size: {
      default: 'size-15',
      sm: 'size-6',
      xs: 'size-4',
    },
  },
  defaultVariants: { state: 'empty', size: 'default' },
});

type StarIconProps = React.ComponentProps<'img'> & VariantProps<typeof starIconVariants>;

function StarIcon({ state, size, className, ...props }: StarIconProps) {
  return (
    <img
      data-slot="star-icon"
      src={
        state === 'filled'
          ? '/assets/gamification/stars.svg'
          : '/assets/gamification/star-empty.svg'
      }
      alt=""
      className={cn(starIconVariants({ state, size }), className)}
      {...props}
    />
  );
}

const MAX_STARS = 3;
const STAR_INDICES = Array.from({ length: MAX_STARS }, (_, i) => i + 1);

function StarRatingLabel({ value }: { value: number }) {
  const t = useT();
  switch (value) {
    case 1:
      return t('Needs support');
    case 2:
      return t('Good work');
    case 3:
      return t('Excellent work');
    default:
      return t('Not acceptable');
  }
}

type StarRatingProps = {
  value: number;
  onChange?: (value: number) => void;
  lockedStars?: number;
  size?: Exclude<VariantProps<typeof starIconVariants>['size'], 'xs' | null>;
  className?: string;
};

function StarRating({ value, onChange, lockedStars = 0, size, className }: StarRatingProps) {
  const t = useT();

  function handleClick(starIndex: number) {
    if (!onChange) return;
    if (starIndex < lockedStars) return;
    const newValue = starIndex === value ? lockedStars : starIndex;
    if (newValue === value) return;
    onChange(newValue);
  }

  return (
    <div data-slot="star-rating" className={cn('flex flex-col items-center gap-sm', className)}>
      <div
        className="flex gap-sm"
        role={onChange ? 'group' : undefined}
        aria-label={onChange ? t('Star rating') : undefined}
      >
        {STAR_INDICES.map(function (starIndex) {
          const isFilled = starIndex <= value;

          const star = <StarIcon state={isFilled ? 'filled' : 'empty'} size={size} />;

          if (onChange) {
            const button = (
              <button
                key={starIndex}
                type="button"
                aria-pressed={isFilled}
                aria-label={t('{starIndex} star', { starIndex })}
                className="
                  cursor-pointer rounded-full outline-none
                  hover:opacity-80
                  focus-visible:ring-[3px] focus-visible:ring-ring/50
                "
                onClick={() => handleClick(starIndex)}
              >
                {star}
              </button>
            );

            if (starIndex === value && value === lockedStars) {
              return (
                <Tooltip key={starIndex}>
                  <TooltipTrigger asChild>{button}</TooltipTrigger>
                  <TooltipContent>{t('Previously given stars cannot be removed')}</TooltipContent>
                </Tooltip>
              );
            }

            return button;
          }

          return <span key={starIndex}>{star}</span>;
        })}
      </div>
      <span className="text-body-big text-text-900">
        <StarRatingLabel value={value} />
      </span>
    </div>
  );
}

type StarDisplayProps = {
  stars: number;
  size?: VariantProps<typeof starIconVariants>['size'];
  className?: string;
};

function StarDisplay({ stars, size = 'sm', className }: StarDisplayProps) {
  return (
    <div className={cn('flex gap-xs', className)}>
      {STAR_INDICES.map(i => (
        <StarIcon key={i} state={i <= stars ? 'filled' : 'empty'} size={size} />
      ))}
    </div>
  );
}

export { MAX_STARS, StarDisplay, StarIcon, starIconVariants, StarRating };
export type { StarDisplayProps, StarIconProps, StarRatingProps };

export function StarText({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-xs text-yellow-700">
      <StarIcon state="filled" size="sm" /> {children}
    </span>
  );
}
